// src/pages/seasons/Seasons.jsx
'use client';

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useTeam } from '@/context/TeamContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import {
  PlusIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  LinkIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import TeamSubNav from '@/components/team/TeamSubNav';
import { toSeasonDate } from '@/utils/seasonDates';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* Status pill — semantic palette:
 *   open      → lime (active registration)
 *   closed    → zinc (registration closed)
 *   draft     → zinc soft
 *   archived  → zinc outline
 */
function StatusPill({ status }) {
  const map = {
    open: {
      label: 'Open',
      cls: 'bg-[#b5f03d] text-[#0d1f14] ring-1 ring-[#94cc28]',
    },
    closed: {
      label: 'Closed',
      cls: 'bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200',
    },
    draft: {
      label: 'Draft',
      cls: 'bg-zinc-50 text-zinc-500 ring-1 ring-zinc-200',
    },
    archived: {
      label: 'Archived',
      cls: 'bg-white text-zinc-500 ring-1 ring-zinc-300',
    },
  };
  const item = map[status] ?? map.draft;
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] shrink-0',
        item.cls
      )}
      style={{ fontFamily: HEAD_FONT }}
    >
      {item.label}
    </span>
  );
}

function formatFee(cents) {
  if (!cents) return 'Free';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

// Season dates are date-only — shared convention keeps them on the
// calendar day the coach picked (see src/utils/seasonDates.js).
const toDate = toSeasonDate;

function GhostBtn({ onClick, children, className = '', title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all',
        className
      )}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
    >
      {children}
    </button>
  );
}

function PlainBtn({ onClick, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold uppercase text-zinc-600 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-all',
        className
      )}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
    >
      {children}
    </button>
  );
}

function SeasonCard({ season, onToggleStatus, onArchive, regCount }) {
  const navigate = useNavigate();
  const regOpen      = toDate(season.registrationOpen);
  const regClose     = toDate(season.registrationClose);
  const isOpen       = season.status === 'open';
  const isArchived   = season.status === 'archived';
  const count        = regCount ?? 0;
  const maxPlayers   = season.maxPlayers ?? null;
  const isFilling    = maxPlayers && count >= Math.ceil(maxPlayers * 0.8);

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 transition-all overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link to={`/seasons/${season.id}`} className="group">
              <h3
                className="text-lg font-bold uppercase tracking-tight text-[#0d1f14] truncate group-hover:text-[#5a8a1f] transition-colors"
                style={{ fontFamily: HEAD_FONT }}
              >
                {season.name}
              </h3>
            </Link>
            {regOpen && regClose && (
              <p className="mt-0.5 text-xs text-zinc-500">
                Registration: {format(regOpen, 'MMM d')} – {format(regClose, 'MMM d, yyyy')}
              </p>
            )}
          </div>
          <StatusPill status={season.status} />
        </div>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-zinc-600">
          <span className="flex items-center gap-1.5">
            <CurrencyDollarIcon className="h-4 w-4 text-zinc-400" />
            <span className="text-[#0d1f14] font-medium">{formatFee(season.registrationFee)}</span>
          </span>
          {season.maxPlayers && (
            <span className="flex items-center gap-1.5">
              <UserGroupIcon className="h-4 w-4 text-zinc-400" />
              Max {season.maxPlayers} players
            </span>
          )}
          {season.seasonStart && season.seasonEnd && (
            <span className="flex items-center gap-1.5">
              <CalendarDaysIcon className="h-4 w-4 text-zinc-400" />
              {format(toDate(season.seasonStart), 'MMM d')} – {format(toDate(season.seasonEnd), 'MMM d, yyyy')}
            </span>
          )}
          <span className={clsx(
            'flex items-center gap-1.5 font-medium',
            isFilling ? 'text-amber-600' : 'text-zinc-600'
          )}>
            <UserGroupIcon className={clsx('h-4 w-4', isFilling ? 'text-amber-500' : 'text-zinc-400')} />
            {maxPlayers ? `${count} / ${maxPlayers} registered` : `${count} registered`}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-zinc-200 px-5 py-3 bg-[#f7faf5]">
        <div className="flex items-center gap-1">
          {!isArchived && (
            <PlainBtn onClick={() => onToggleStatus(season)}>
              {isOpen ? 'Close Reg' : 'Open Reg'}
            </PlainBtn>
          )}
          <PlainBtn onClick={() => onArchive(season)}>
            {isArchived
              ? <><ArrowUturnLeftIcon className="h-3.5 w-3.5" />Unarchive</>
              : <><ArchiveBoxIcon className="h-3.5 w-3.5" />Archive</>
            }
          </PlainBtn>
        </div>
        {!isArchived && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <PlainBtn
              onClick={() => {
                const url = `${window.location.origin}/register/${season.teamId}/${season.id}`;
                navigator.clipboard.writeText(url).then(() => toast.success('Registration link copied!'));
              }}
            >
              <LinkIcon className="h-3.5 w-3.5" />
              Copy Link
            </PlainBtn>
            <GhostBtn onClick={() => navigate(`/seasons/${season.id}/attendance`)}>
              <ClipboardDocumentCheckIcon className="h-3.5 w-3.5" />
              Attendance
            </GhostBtn>
            <GhostBtn onClick={() => navigate(`/seasons/${season.id}/registrations`)}>
              <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
              Registrations
            </GhostBtn>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Seasons() {
  const { currentTeam } = useTeam();
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regCounts, setRegCounts] = useState({});
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!currentTeam?.id) {
      setSeasons([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'seasons'),
      where('teamId', '==', currentTeam.id)
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.createdAt?.seconds ?? 0;
          const tb = b.createdAt?.seconds ?? 0;
          return tb - ta;
        });
      setSeasons(list);
      setLoading(false);
    }, (err) => {
      console.error('[Seasons]', err);
      setLoading(false);
    });
    return unsub;
  }, [currentTeam?.id]);

  useEffect(() => {
    if (!currentTeam?.id) return;
    const q = query(
      collection(db, 'registrations'),
      where('teamId', '==', currentTeam.id)
    );
    const unsub = onSnapshot(q, (snap) => {
      const counts = {};
      snap.forEach((d) => {
        const data = d.data();
        if (data.status === 'cancelled') return;
        const sid = data.seasonId;
        counts[sid] = (counts[sid] || 0) + 1;
      });
      setRegCounts(counts);
    }, (err) => console.error('[Seasons] regCounts', err));
    return unsub;
  }, [currentTeam?.id]);

  const handleToggleStatus = async (season) => {
    const next = season.status === 'open' ? 'closed' : 'open';
    try {
      await updateDoc(doc(db, 'seasons', season.id), {
        status: next,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Registration ${next === 'open' ? 'opened' : 'closed'}.`);
    } catch {
      toast.error('Failed to update status.');
    }
  };

  const handleArchive = async (season) => {
    const isArchived = season.status === 'archived';
    const next = isArchived ? 'closed' : 'archived';
    try {
      await updateDoc(doc(db, 'seasons', season.id), {
        status: next,
        updatedAt: serverTimestamp(),
      });
      toast.success(isArchived ? 'Season restored.' : 'Season archived.');
    } catch {
      toast.error('Failed to update season.');
    }
  };

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-10">

        {/* Team sub-nav: keep Overview / Roster / Seasons / Schedule
            / Chat tabs visible whenever a team is selected. Without
            this, users who land here from /seasons/:id back-buttons
            (when season.teamId is missing or hasn't loaded) lose the
            team context entirely. */}
        {currentTeam?.id && <TeamSubNav teamId={currentTeam.id} />}

        {/* Header */}
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p
              className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Seasons
            </p>
            <h1
              className="mt-1 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {currentTeam?.name ?? 'Your team'} seasons
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Manage registration seasons for {currentTeam?.name ?? 'your team'}.
            </p>
          </div>
          <button
            onClick={() => navigate('/seasons/create')}
            className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            <PlusIcon className="h-4 w-4" />
            New Season
          </button>
        </header>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((n) => (
              <div key={n} className="h-32 rounded-2xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        ) : (() => {
          const archivedSeasons  = seasons.filter(s => s.status === 'archived');
          const activeSeasons    = seasons.filter(s => s.status !== 'archived');
          const visibleSeasons   = showArchived ? archivedSeasons : activeSeasons;
          const hasArchived      = archivedSeasons.length > 0;

          return (
            <>
              {/* Active / Archived toggle */}
              {hasArchived && (
                <div className="mb-5 flex items-center gap-2">
                  <button
                    onClick={() => setShowArchived(false)}
                    className={clsx(
                      'px-3.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all',
                      !showArchived
                        ? 'bg-[#0d1f14] text-white shadow-[0_1px_2px_0_rgba(13,31,20,0.12)]'
                        : 'bg-white border border-zinc-200 text-zinc-600 hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 hover:text-[#0d1f14]'
                    )}
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    Active ({activeSeasons.length})
                  </button>
                  <button
                    onClick={() => setShowArchived(true)}
                    className={clsx(
                      'px-3.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all flex items-center gap-1.5',
                      showArchived
                        ? 'bg-[#0d1f14] text-white shadow-[0_1px_2px_0_rgba(13,31,20,0.12)]'
                        : 'bg-white border border-zinc-200 text-zinc-600 hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 hover:text-[#0d1f14]'
                    )}
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    <ArchiveBoxIcon className="h-3.5 w-3.5" />
                    Archived ({archivedSeasons.length})
                  </button>
                </div>
              )}

              {visibleSeasons.length === 0 ? (
                <div className="rounded-2xl border border-zinc-200 bg-white p-10 sm:p-12 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                    <CalendarDaysIcon className="h-7 w-7 text-[#5a8a1f]" />
                  </div>
                  <h2
                    className="text-xl font-bold uppercase tracking-tight text-[#0d1f14]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {showArchived ? 'No archived seasons' : 'No seasons yet'}
                  </h2>
                  <p className="mt-1.5 text-sm text-zinc-500 max-w-md mx-auto">
                    {showArchived
                      ? 'Seasons you archive will appear here.'
                      : 'Create your first season to start collecting registrations.'}
                  </p>
                  {!showArchived && (
                    <button
                      onClick={() => navigate('/seasons/create')}
                      className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                    >
                      <PlusIcon className="h-4 w-4" />
                      Create First Season
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {visibleSeasons.map((s) => (
                    <SeasonCard
                      key={s.id}
                      season={s}
                      onToggleStatus={handleToggleStatus}
                      onArchive={handleArchive}
                      regCount={regCounts[s.id] ?? 0}
                    />
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
